import React, { useState, useEffect, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import '../EditProfile/EditProfile.css';
import { useForm } from 'react-hook-form';
import axios from "axios";
import AvatarEditor from 'react-avatar-edit';
import image63 from '../img/user.png';
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import countriesData from '../../Data/countries.json';
import { ChevronLeft, Camera, Plus, X, Save, Share2 } from 'lucide-react';
import { ayreneService, AyreneProfileUpdateRequest } from '../../services/ayrene.service';

interface ProfileData {
  [key: string]: any;
  firstName?: string;
  phoneNumber?: string;
  address?: string;
  annualIncome?: string;
  city?: string;
  country?: string;
  age?: string;
  education?: string;
  familyDetails?: string;
  gender?: string;
  height?: string;
  weight?: string;
  hobbiesAndInterests?: string;
  maritalStatus?: string;
  primaryLanguage?: string;
  occupation?: string;
  religion?: string;
  state?: string;
  preferredPartnerAgeRange?: string;
  preferredPartnerLocation?: string;
  preferredPartnerReligion?: string;
  profilePictureUrl?: string;
  aboutMe?: string;
  userId?: string;
}

interface ImageItem {
  imageId: string | null;
  url: string;
  file: File | null;
}

const ProfileEdit: React.FC = () => {
  const {
    register,
    clearErrors,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      FirstName: '',
      AboutMe: '',
      Address: '',
      AnnualIncome: '',
      City: '',
      Country: '',
      Age: '',
      Education: '',
      FamilyDetails: '',
      Gender: '',
      Height: '',
      Weight: '',
      HobbiesAndInterests: '',
      MaritalStatus: '',
      PrimaryLanguage: '',
      Occupation: '',
      Religion: '',
      State: '',
      PreferredPartnerAgeRange: '',
      PreferredPartnerLocation: '',
      PreferredPartnerReligion: '',
    }
  });

  const [initialData, setInitialData] = useState<ProfileData>({});
  const [profileImage, setProfileImage] = useState<File | string | null>(null);
  const [profileImages, setProfileImages] = useState<ImageItem[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [profileImagePresent, setProfileImagePresent] = useState<string | null>(null);
  const navigateTo = useNavigate();
  const [aboutMe, setAboutMe] = useState<string>("");
  const [wordCount, setWordCount] = useState<number>(0);
  const maxWords = 50;

  // State for dynamic country/state/city dropdowns
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [showCustomCountry, setShowCustomCountry] = useState(false);
  const [showCustomState, setShowCustomState] = useState(false);
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCountry, setCustomCountry] = useState('');
  const [customState, setCustomState] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [showCustomPartnerLocation, setShowCustomPartnerLocation] = useState(false);
  const [customPartnerLocation, setCustomPartnerLocation] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string>("");

  const countryValue = watch('Country');
  const stateValue = watch('State');
  const cityValue = watch('City');
  const partnerLocationValue = watch('PreferredPartnerLocation');
  
  const sanitizeValue = (value: string | null | undefined): string => {
    return value === "N/A" ? "" : value || "";
  };

  const convertAgeToBirthDate = (age: string | number | undefined): string | undefined => {
    if (!age) return undefined;

    const ageNum = typeof age === 'string' ? parseInt(age, 10) : age;
    if (isNaN(ageNum) || ageNum <= 0 || ageNum > 120) return undefined;

    const currentYear = new Date().getFullYear();
    const birthYear = currentYear - ageNum;
    const dateOfBirth = `${birthYear}-01-01`;

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateOfBirth)) return undefined;

    return dateOfBirth;
  };

  const getEmailFromAuth = (): string | undefined => {
    const token = localStorage.getItem('authToken');
    if (!token) return undefined;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.email || payload.sub || undefined;
    } catch (error) {
      console.error('Error parsing auth token for email:', error);
      return undefined;
    }
  };

  const prepareAyreneProfileData = (fervoirData: any, profilePictureUrl?: string): AyreneProfileUpdateRequest => {
    const dateOfBirth = convertAgeToBirthDate(fervoirData.Age);

    const normalizeGender = (gender: string | undefined): string | undefined => {
      if (!gender) return undefined;
      const lowerGender = gender.toLowerCase();
      if (lowerGender === 'male' || lowerGender === 'female' || lowerGender === 'other') {
        return lowerGender;
      }
      return undefined;
    };

    const ayreneData: AyreneProfileUpdateRequest = {
      username: fervoirData.FirstName || undefined,
      gender: normalizeGender(fervoirData.Gender),
      dateOfBirth: dateOfBirth,
      address: fervoirData.Address || undefined
    };

    Object.keys(ayreneData).forEach(key => {
      if (ayreneData[key as keyof AyreneProfileUpdateRequest] === undefined) {
        delete ayreneData[key as keyof AyreneProfileUpdateRequest];
      }
    });

    return ayreneData;
  };

  const syncToAyrene = async (fervoirData: any, profilePictureUrl?: string): Promise<void> => {
    const ayreneToken = localStorage.getItem('ayreneAuthToken');
    if (!ayreneToken) {
      console.log('ℹ️ No Ayrene token found, skipping sync');
      return;
    }

    try {
      const ayreneProfileData = prepareAyreneProfileData(fervoirData, profilePictureUrl);

      console.log('🔄 Syncing profile to Ayrene...');
      console.log('📤 Ayrene sync data:', ayreneProfileData);

      const result = await ayreneService.updateUserProfile(ayreneProfileData);

      if (result.success) {
        console.log('✅ Profile synced to Ayrene successfully!');
      } else {
        console.warn('⚠️ Ayrene sync failed:', result.message);
      }
    } catch (error) {
      console.error('❌ Error during Ayrene sync:', error);
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get('https://nrimarriage.in/api/v1/users/Profile', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 200) {
          const profileData: ProfileData = response.data;
          setUserId(profileData.userId || null);
          setInitialData(profileData);

          const mappedData = {
            FirstName: sanitizeValue(profileData.firstName),
            Address: sanitizeValue(profileData.address),
            AnnualIncome: sanitizeValue(profileData.annualIncome),
            City: sanitizeValue(profileData.city),
            Country: sanitizeValue(profileData.country),
            Age: sanitizeValue(profileData.age),
            Education: sanitizeValue(profileData.education),
            FamilyDetails: sanitizeValue(profileData.familyDetails),
            Gender: sanitizeValue(profileData.gender),
            Height: sanitizeValue(profileData.height),
            Weight: sanitizeValue(profileData.weight),
            HobbiesAndInterests: sanitizeValue(profileData.hobbiesAndInterests),
            MaritalStatus: sanitizeValue(profileData.maritalStatus),
            PrimaryLanguage: sanitizeValue(profileData.primaryLanguage),
            Occupation: sanitizeValue(profileData.occupation),
            Religion: sanitizeValue(profileData.religion),
            State: sanitizeValue(profileData.state),
            PreferredPartnerAgeRange: sanitizeValue(profileData.preferredPartnerAgeRange),
            PreferredPartnerLocation: sanitizeValue(profileData.preferredPartnerLocation),
            PreferredPartnerReligion: sanitizeValue(profileData.preferredPartnerReligion),
            AboutMe: sanitizeValue(profileData.aboutMe),
          };

          reset(mappedData);

          if (profileData.profilePictureUrl) {
            setProfileImage(profileData.profilePictureUrl);
            setProfileImagePresent(profileData.profilePictureUrl);
          }

          if (profileData.aboutMe && profileData.aboutMe !== "N/A") {
            const words = profileData.aboutMe.trim().split(/\s+/);
            const limitedText = words.slice(0, maxWords).join(" ");
            setAboutMe(limitedText);
            setWordCount(words.length > maxWords ? maxWords : words.length);
          } else {
            setAboutMe("");
            setWordCount(0);
          }

          setSelectedCountry(sanitizeValue(profileData.country));
          setSelectedState(sanitizeValue(profileData.state));
          setSelectedCity(sanitizeValue(profileData.city));
          
          const currentPartnerLocation = sanitizeValue(profileData.preferredPartnerLocation);
          const partnerLocationExists = countriesData.some(c => c.name === currentPartnerLocation);
          if (!partnerLocationExists && currentPartnerLocation) {
            setShowCustomPartnerLocation(true);
            setCustomPartnerLocation(currentPartnerLocation);
            setValue('PreferredPartnerLocation', currentPartnerLocation);
          } else if (currentPartnerLocation) {
            setValue('PreferredPartnerLocation', currentPartnerLocation);
          }
          
          const countryExists = countriesData.some(c => c.name === sanitizeValue(profileData.country));
          if (!countryExists && sanitizeValue(profileData.country)) {
            setShowCustomCountry(true);
            setCustomCountry(sanitizeValue(profileData.country));
            setValue('Country', sanitizeValue(profileData.country));
          } else if (sanitizeValue(profileData.country)) {
            setSelectedCountry(sanitizeValue(profileData.country));
          }
          
          const currentCountry = sanitizeValue(profileData.country);
          const currentState = sanitizeValue(profileData.state);
          const currentCity = sanitizeValue(profileData.city);
          
          if (currentCountry) {
            const country = countriesData.find(c => c.name === currentCountry);
            if (country) {
              const stateExists = country.states.some(s => s.name === currentState);
              if (!stateExists && currentState) {
                setShowCustomState(true);
                setCustomState(currentState);
                setValue('State', currentState);
              } else if (currentState) {
                setSelectedState(currentState);
              }
              
              const state = country.states.find(s => s.name === currentState);
              if (state) {
                const cityExists = state.cities.includes(currentCity);
                if (!cityExists && currentCity) {
                  setShowCustomCity(true);
                  setCustomCity(currentCity);
                  setValue('City', currentCity);
                } else if (currentCity) {
                  setSelectedCity(currentCity);
                }
              } else if (currentCity) {
                setShowCustomCity(true);
                setCustomCity(currentCity);
                setValue('City', currentCity);
              }
            } else {
              if (currentState) {
                setShowCustomState(true);
                setCustomState(currentState);
                setValue('State', currentState);
              }
              if (currentCity) {
                setShowCustomCity(true);
                setCustomCity(currentCity);
                setValue('City', currentCity);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching profile data:', err);
        alert('An error occurred while fetching the profile data.');
      }
    };

    fetchProfileData();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = e.target.value;
    const words = text.trim().split(/\s+/);

    if (words.length <= maxWords) {
      setAboutMe(text);
      setWordCount(words.length);
      setValue("AboutMe", text);
    }
  };

  const handleProfileSubmit = async (data: any) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("User is not authenticated.");
        return;
      }

      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const formattedValue =
            typeof value === "object" ? JSON.stringify(value) : value.toString();
          formData.append(key, formattedValue);
        }
      });

      if (profileImage && typeof profileImage !== 'string') {
        formData.append("ProfileImage", profileImage);
      }

      profileImages.forEach((image) => {
        if (image.file) formData.append("ProfileImages", image.file);
      });

      const response = await axios.put("https://nrimarriage.in/api/v1/users/Profile", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        alert("Profile updated successfully.");
        localStorage.setItem("profileUpdated", "true");

        let profilePictureUrl = profileImagePresent || undefined;

        if (response.data?.profilePictureUrl) {
          profilePictureUrl = response.data.profilePictureUrl;
        }

        await syncToAyrene(data, profilePictureUrl);

        navigateTo("/home");
      }
    } catch (error: any) {
      console.error("❌ Error updating profile:", error);
      alert(error.response?.data?.message || "An error occurred while updating the profile.");
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setSelectedImageSrc(imageUrl);
      setShowCropModal(true);
    }
  };

  const handleReset = () => {
    reset();
    setTimeout(() => {
      setValue("FirstName", "");
      setValue("AboutMe", "");
      setValue("Address", "");
      setValue("AnnualIncome", "");
      setValue("City", "");
      setValue("Country", "");
      setValue("Age", "");
      setValue("Education", "");
      setValue("FamilyDetails", "");
      setValue("Gender", "");
      setValue("Height", "");
      setValue("Weight", "");
      setValue("HobbiesAndInterests", "");
      setValue("MaritalStatus", "");
      setValue("PrimaryLanguage", "");
      setValue("Occupation", "");
      setValue("Religion", "");
      setValue("State", "");
      setValue("PreferredPartnerAgeRange", "");
      setValue("PreferredPartnerLocation", "");
      setValue("PreferredPartnerReligion", "");
    }, 0);

    setProfileImage(null);
    setProfileImagePresent(null);
    setProfileImages([]);
    setAboutMe('');
    setWordCount(0);  
    setSelectedCountry('');
    setSelectedState('');
    setShowCustomCountry(false);
    setShowCustomState(false);
    setShowCustomCity(false);
    setShowCustomPartnerLocation(false);
    setCustomCountry('');
    setCustomState('');
    setCustomCity('');
    setCustomPartnerLocation('');
  };

  const onCrop = (preview: string) => {
    setCroppedImage(preview);
  };

  const onClose = () => {
    setCroppedImage(null);
  };

  const onBeforeFileLoad = (elem: any) => {
    const file = elem.target.files?.[0];
    if (file && file.size > 2 * 1024 * 1024) {
      alert("File is too big! Max size is 2MB.");
      elem.target.value = "";
    }
  };

  const handleCropSave = () => {
    if (croppedImage) {
      fetch(croppedImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
          setProfileImage(file);
          setProfileImagePresent(croppedImage);
          if (selectedImageSrc) URL.revokeObjectURL(selectedImageSrc);
          setSelectedImageSrc(null);
          setShowCropModal(false);
        });
    }
  };

  const handleCropCancel = () => {
    if (selectedImageSrc) URL.revokeObjectURL(selectedImageSrc);
    setSelectedImageSrc(null);
    setShowCropModal(false);
  };

  useEffect(() => {
    const fetchAdditionalImages = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token || !userId) return;

        const response = await axios.get(
          `https://nrimarriage.in/api/v1/users/GetUserImagesWithProfile?userId=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          const fetchedImages = response.data.allImages || [];
          const formattedImages = fetchedImages.map((img: any) => ({
            imageId: img.imageId,
            url: img.imageUrl,
            file: null,
          }));

          setProfileImages(formattedImages);
        }
      } catch (error) {
        console.error("❌ Error fetching additional images:", error);
      }
    };

    if (userId) {
      fetchAdditionalImages();
    }
  }, [userId]);

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (profileImages.length >= 4) {
      alert("You can upload a maximum of 4 images.");
      return;
    }

    const files = Array.from(event.target.files || []);
    const newImages = files.map((file) => ({
      imageId: null,
      url: URL.createObjectURL(file),
      file,
    }));

    setProfileImages((prevImages) => [...prevImages, ...newImages].slice(0, 4));
  };

  const handleRemoveImage = async (index: number) => {
    const imageToRemove = profileImages[index];

    if (imageToRemove.imageId) {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.delete(
          `https://nrimarriage.in/api/v1/users/DeleteUserImage?imageId=${imageToRemove.imageId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          setProfileImages((prevImages) => prevImages.filter((_, i) => i !== index));
        } else {
          alert("Failed to delete image. Please try again.");
        }
      } catch (error) {
        console.error("❌ Error deleting image:", error);
        alert("Failed to delete image. Please try again.");
      }
    } else {
      setProfileImages((prevImages) => prevImages.filter((_, i) => i !== index));
    }
  };

  const getStatesForSelectedCountry = () => {
    const country = countriesData.find(c => c.name === selectedCountry);
    return country ? country.states : [];
  };

  const getCitiesForSelectedState = () => {
    const country = countriesData.find(c => c.name === selectedCountry);
    if (country) {
      const state = country.states.find(s => s.name === selectedState);
      return state ? state.cities : [];
    }
    return [];
  };

  const handleShareClick = () => {
    const profileLink = `${window.location.origin}/profile/${userId}`;
    setShareLink(profileLink);
    setShowSharePopup(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      alert("Link copied to clipboard!");
      setShowSharePopup(false);
    });
  };

  return (
    <div className="edit-profile-container">
      <Header onSearchClick={() => console.log("Search button clicked")} />

      <header className="edit-profile-header">
        <button className="back-button1" onClick={() => navigateTo('/home')}>
          <ChevronLeft size={24} />
        </button>
        <h1 className="header-title">Edit Profile</h1>
        <div className="header-actions">
          <button className="share-button-header" onClick={handleShareClick}>
            <Share2 size={20} />
          </button>
          <button className="save-button-header" onClick={handleSubmit(handleProfileSubmit)}>
            <Save size={20} style={{ marginRight: '0.5rem' }} /> Save
          </button>
        </div>
      </header>

      <div className="edit-profile-content">
        <form onSubmit={handleSubmit(handleProfileSubmit)}>
          {/* Profile Photo Section */}
          <section className="profile-photo-section">
            <div className="photo-upload-area">
              <label htmlFor="ProfileImage">
                <img
                  src={profileImagePresent || (typeof profileImage === 'string' ? profileImage : '') || image63}
                  alt="Profile Preview"
                  className="main-profile-image"
                />
              </label>
              <input
                type="file"
                id="ProfileImage"
                className="hidden"
                accept="image/*"
                onChange={onFileChange}
              />
              <button type="button" className="photo-edit-overlay" onClick={() => document.getElementById('ProfileImage')?.click()}>
                <Camera size={20} />
              </button>
            </div>

            <div className="additional-photos-section">
              <h3 className="additional-photos-title">Additional Photos</h3>
              <div className="additional-photos-grid">
                {profileImages.map((image, index) => (
                  <div key={index} className="photo-slot">
                    <img src={image.url} alt={`Upload ${index + 1}`} />
                    <button className="remove-photo-btn" type="button" onClick={() => handleRemoveImage(index)}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {profileImages.length < 4 && (
                  <label className="photo-slot empty">
                    <Plus size={32} />
                    <input type="file" accept="image/*" onChange={handleImageUpload} multiple hidden />
                  </label>
                )}
              </div>
            </div>
          </section>

          {/* About Me Section */}
          <section className="form-section">
            <h3 className="section-title1">About Me</h3>
            <div className="form-grid">
              <div className="form-field full-width">
                <label htmlFor="AboutMe" className="field-label">Tell us about yourself</label>
                <textarea
                  id="AboutMe"
                  placeholder="Enter your info"
                  className="field-textarea"
                  {...register("AboutMe")}
                  value={aboutMe}
                  onChange={handleChange}
                  rows={4}
                />
                <p className={`word-counter ${wordCount > maxWords * 0.8 ? 'warning' : ''} ${wordCount >= maxWords ? 'danger' : ''}`}>
                  {wordCount}/{maxWords} words
                </p>
              </div>
            </div>
          </section>

          {/* Personal Details Section */}
          <section className="form-section">
            <h3 className="section-title1">Personal Details</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="FirstName" className="field-label">First Name</label>
                <input type="text" id="FirstName" placeholder="Enter your First Name" className="field-input" {...register('FirstName')} />
              </div>
              <div className="form-field">
                <label htmlFor="Age" className="field-label">Age</label>
                <input type="number" id="Age" placeholder="Enter your Age" className="field-input" {...register('Age')} />
              </div>
              <div className="form-field">
                <label htmlFor="Gender" className="field-label">Gender</label>
                <select id="Gender" className="field-select" {...register('Gender')} >
                  <option value="">--Please Select--</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="MaritalStatus" className="field-label">Marital Status</label>
                <select id="MaritalStatus" className="field-select" {...register('MaritalStatus')}>
                  <option value="">--Select Marital Status--</option>
                  <option value="Single">Single</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="Height" className="field-label">Height (cm)</label>
                <input type="number" id="Height" placeholder="Enter your Height in cm" className="field-input" {...register('Height')} />
              </div>
              <div className="form-field">
                <label htmlFor="Weight" className="field-label">Weight (kg)</label>
                <input type="number" id="Weight" placeholder="Enter your Weight in kg" className="field-input" {...register('Weight')} />
              </div>
              <div className="form-field">
                <label htmlFor="PrimaryLanguage" className="field-label">Zodiac Sign</label>
                <select id="PrimaryLanguage" className="field-select" {...register('PrimaryLanguage')} >
                  <option value="">--Select Zodiac Sign--</option>
                  <option value="Aries">Aries</option>
                  <option value="Taurus">Taurus</option>
                  <option value="Gemini">Gemini</option>
                  <option value="Cancer">Cancer</option>
                  <option value="Leo">Leo</option>
                  <option value="Virgo">Virgo</option>
                  <option value="Libra">Libra</option>
                  <option value="Scorpio">Scorpio</option>
                  <option value="Sagittarius">Sagittarius</option>
                  <option value="Capricorn">Capricorn</option>
                  <option value="Aquarius">Aquarius</option>
                  <option value="Pisces">Pisces</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="Religion" className="field-label">Religion</label>
                <select id="Religion" className="field-select" {...register('Religion')} >
                  <option value="">--Select Religion--</option>
                  <option value="Christian">Christian</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </section>

          {/* Location Details Section */}
          <section className="form-section">
            <h3 className="section-title1">Location Details</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="Country" className="field-label">Country</label>
                <select
                  id="Country"
                  className="field-select"
                  value={showCustomCountry ? 'Others' : countryValue || ''}
                  onChange={(e) => {
                    if (e.target.value === 'Others') {
                      setShowCustomCountry(true);
                      setSelectedCountry('');
                      setValue('Country', customCountry);
                    } else {
                      setShowCustomCountry(false);
                      setCustomCountry('');
                      setSelectedCountry(e.target.value);
                      setValue('Country', e.target.value);
                    }
                    setSelectedState('');
                    setValue('State', '');
                    setValue('City', '');
                    setShowCustomState(false);
                    setShowCustomCity(false);
                    setCustomState('');
                    setCustomCity('');
                  }}
                >
                  <option value="">--Select Country--</option>
                  {countriesData.map((country) => (
                    <option key={country.name} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                  <option value="Others">Others</option>
                </select>
                <input type="hidden" {...register('Country')} value={showCustomCountry ? customCountry : countryValue || ''} />
                {showCustomCountry && (
                  <input
                    type="text"
                    placeholder="Enter your country"
                    value={customCountry}
                    onChange={(e) => {
                      setCustomCountry(e.target.value);
                      setValue('Country', e.target.value);
                    }}
                    className="field-input custom-input"
                  />
                )}
              </div>

              <div className="form-field">
                <label htmlFor="State" className="field-label">State</label>
                <select
                  id="State"
                  className="field-select"
                  value={showCustomState ? 'Others' : stateValue || ''}
                  onChange={(e) => {
                    if (e.target.value === 'Others') {
                      setShowCustomState(true);
                      setSelectedState('');
                      setValue('State', customState);
                    } else {
                      setShowCustomState(false);
                      setCustomState('');
                      setSelectedState(e.target.value);
                      setValue('State', e.target.value);
                    }
                    setSelectedCity('');
                    setValue('City', '');
                    setShowCustomCity(false);
                    setCustomCity('');
                  }}
                  disabled={!selectedCountry && !showCustomCountry}
                >
                  <option value="">--Select State--</option>
                  {getStatesForSelectedCountry().map((state) => (
                    <option key={state.name} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                  <option value="Others">Others</option>
                </select>
                <input type="hidden" {...register('State')} value={showCustomState ? customState : stateValue || ''} />
                {showCustomState && (
                  <input
                    type="text"
                    placeholder="Enter your state"
                    value={customState}
                    onChange={(e) => {
                      setCustomState(e.target.value);
                      setValue('State', e.target.value);
                    }}
                    className="field-input custom-input"
                  />
                )}
              </div>

              <div className="form-field">
                <label htmlFor="City" className="field-label">City</label>
                <select
                  id="City"
                  className="field-select"
                  value={showCustomCity ? 'Others' : cityValue || ''}
                  onChange={(e) => {
                    if (e.target.value === 'Others') {
                      setShowCustomCity(true);
                      setSelectedCity('');
                      setValue('City', customCity);
                    } else {
                      setShowCustomCity(false);
                      setCustomCity('');
                      setSelectedCity(e.target.value);
                      setValue('City', e.target.value);
                    }
                  }}
                  disabled={!selectedState && !showCustomState}
                >
                  <option value="">--Select City--</option>
                  {getCitiesForSelectedState().map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                  <option value="Others">Others</option>
                </select>
                <input type="hidden" {...register('City')} value={showCustomCity ? customCity : cityValue || ''} />
                {showCustomCity && (
                  <input
                    type="text"
                    placeholder="Enter your city"
                    value={customCity}
                    onChange={(e) => {
                      setCustomCity(e.target.value);
                      setValue('City', e.target.value);
                    }}
                    className="field-input custom-input"
                  />
                )}
              </div>
              <div className="form-field">
                <label htmlFor="Address" className="field-label">Address</label>
                <input type="text" id="Address" placeholder="Enter your Address" className="field-input" {...register('Address')} />
              </div>
            </div>
          </section>

          {/* Professional & Financial Details Section */}
          <section className="form-section">
            <h3 className="section-title1">Professional & Financial</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="Occupation" className="field-label">Occupation</label>
                <select id="Occupation" className="field-select" {...register('Occupation')} >
                  <option value="">--Please Select--</option>
                  <option value="Software Developer">Software Developer</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Engineer">Engineer</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Nurse">Nurse</option>
                  <option value="Accountant">Accountant</option>
                  <option value="Lawyer">Lawyer</option>
                  <option value="Architect">Architect</option>
                  <option value="Marketing Manager">Marketing Manager</option>
                  <option value="Sales Executive">Sales Executive</option>
                  <option value="Civil Servant">Civil Servant</option>
                  <option value="Researcher">Researcher</option>
                  <option value="Data Analyst">Data Analyst</option>
                  <option value="Business Analyst">Business Analyst</option>
                  <option value="Entrepreneur">Entrepreneur</option>
                  <option value="Consultant">Consultant</option>
                  <option value="Journalist">Journalist</option>
                  <option value="Graphic Designer">Graphic Designer</option>
                  <option value="Content Writer">Content Writer</option>
                  <option value="Photographer">Photographer</option>
                  <option value="Musician">Musician</option>
                  <option value="Actor/Actress">Actor/Actress</option>
                  <option value="Social Worker">Social Worker</option>
                  <option value="Scientist">Scientist</option>
                  <option value="Pilot">Pilot</option>
                  <option value="Chef">Chef</option>
                  <option value="Pharmacist">Pharmacist</option>
                  <option value="Librarian">Librarian</option>
                  <option value="Veterinarian">Veterinarian</option>
                  <option value="Mechanic">Mechanic</option>
                  <option value="Electrician">Electrician</option>
                  <option value="Plumber">Plumber</option>
                  <option value="Farmer">Farmer</option>
                  <option value="Police Officer">Police Officer</option>
                  <option value="Army Officer">Army Officer</option>
                  <option value="Professor">Professor</option>
                  <option value="Clerk">Clerk</option>
                  <option value="Banker">Banker</option>
                  <option value="Dentist">Dentist</option>
                  <option value="Psychologist">Psychologist</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="Education" className="field-label">Highest Education</label>
                <select id="Education" className="field-select" {...register('Education')}>
                  <option value="">--Select Highest Education--</option>
                  <option value="High School">High School</option>
                  <option value="Associate Degree">Associate Degree</option>
                  <option value="Bachelor's Degree">Bachelor's Degree</option>
                  <option value="Master's Degree">Master's Degree</option>
                  <option value="Doctorate (PhD)">Doctorate (PhD)</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Certificate">Certificate</option>
                  <option value="Professional Degree (e.g., MBA, JD, MD)">Professional Degree (e.g., MBA, JD, MD)</option>
                  <option value="Post-Doctorate">Post-Doctorate</option>
                  <option value="Vocational Training">Vocational Training</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="AnnualIncome" className="field-label">Annual Income</label>
                <input type="number" id="AnnualIncome" placeholder="Enter your Annual Income" className="field-input" {...register('AnnualIncome')} />
              </div>
              <div className="form-field">
                <label htmlFor="FamilyDetails" className="field-label">Family Details</label>
                <input type="text" id="FamilyDetails" placeholder="Enter your Family Details" className="field-input" {...register('FamilyDetails')} />
              </div>
            </div>
          </section>

          {/* Hobbies & Interests Section */}
          <section className="form-section">
            <h3 className="section-title1">Hobbies & Interests</h3>
            <div className="form-grid">
              <div className="form-field full-width">
                <label htmlFor="HobbiesAndInterests" className="field-label">Your Hobbies & Interests</label>
                <input type="text" id="HobbiesAndInterests" placeholder="Enter your Hobbies and Interests" className="field-input" {...register('HobbiesAndInterests')} />
              </div>
            </div>
          </section>

          {/* Partner Preferences Section */}
          <section className="form-section">
            <h3 className="section-title1">Partner Preferences</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="PreferredPartnerAgeRange" className="field-label">Preferred Partner Age Range</label>
                <select id="PreferredPartnerAgeRange" className="field-select" {...register('PreferredPartnerAgeRange')} >
                  <option value="">--Select Age Range--</option>
                  <option value="22-25">22-25</option>
                  <option value="26-29">26-29</option>
                  <option value="30-33">30-33</option>
                  <option value="34-37">34-37</option>
                  <option value="38-41">38-41</option>
                  <option value="42-45">42-45</option>
                  <option value="46-50">46-50</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="PreferredPartnerLocation" className="field-label">Preferred Partner Location</label>
                <select
                  id="PreferredPartnerLocation"
                  className="field-select"
                  value={showCustomPartnerLocation ? 'Others' : partnerLocationValue || ''}
                  onChange={(e) => {
                    if (e.target.value === 'Others') {
                      setShowCustomPartnerLocation(true);
                      setValue('PreferredPartnerLocation', customPartnerLocation);
                      clearErrors('PreferredPartnerLocation');
                    } else {
                      setShowCustomPartnerLocation(false);
                      setCustomPartnerLocation('');
                      setValue('PreferredPartnerLocation', e.target.value);
                      clearErrors('PreferredPartnerLocation');
                    }
                  }}
                >
                  <option value="">--Select Country--</option>
                  {countriesData.map((country) => (
                    <option key={country.name} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                  <option value="Others">Others</option>
                </select>
                <input type="hidden" {...register('PreferredPartnerLocation')} value={showCustomPartnerLocation ? customPartnerLocation : partnerLocationValue || ''} />
                {showCustomPartnerLocation && (
                  <input
                    type="text"
                    placeholder="Enter preferred partner location"
                    value={customPartnerLocation}
                    onChange={(e) => {
                      setCustomPartnerLocation(e.target.value);
                      setValue('PreferredPartnerLocation', e.target.value);
                      clearErrors('PreferredPartnerLocation');
                    }}
                    className="field-input custom-input"
                  />
                )}
              </div>
              <div className="form-field">
                <label htmlFor="PreferredPartnerReligion" className="field-label">Preferred Partner Religion</label>
                <select id="PreferredPartnerReligion" className="field-select" {...register('PreferredPartnerReligion')} >
                  <option value="">--Select Religion--</option>
                  <option value="Christian">Christian</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button type="submit" className="btn-primary">
              Update Profile
            </button>
          </div>
        </form>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="crop-modal-overlay1">
          <div className="crop-modal1">
            <div className="crop-modal-header1">
              <h3>Crop Your Profile Picture</h3>
              <button className="crop-close-btn1" onClick={handleCropCancel}>×</button>
            </div>
            <div className="crop-modal-content1">
              <AvatarEditor
                width={300}
                height={300}
                onCrop={onCrop}
                onClose={onClose}
                onBeforeFileLoad={onBeforeFileLoad}
                src={selectedImageSrc || ""}
                shadingColor={'rgba(0,0,0,0.3)'}
                backgroundColor={'#f0f0f0'}
                closeIconColor={'red'}
                shadingOpacity={0.6}
                cropRadius={150}
                cropColor={'#fd5068'}
                lineWidth={3}
              />

            </div>
            <div className="crop-modal-actions1">
              <button className="crop-cancel-btn1" onClick={handleCropCancel}>
                Cancel
              </button>
              <button
                className="crop-save-btn1"
                onClick={handleCropSave}
                disabled={!croppedImage}
              >
                Save Cropped Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Popup */}
      {showSharePopup && (
        <>
          <div className="share-overlay" onClick={() => setShowSharePopup(false)}></div>
          <div className="share-popup">
            <div className="share-popup-header">
              <h4>Share Your Profile</h4>
              <button className="share-close-btn" onClick={() => setShowSharePopup(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="share-popup-content">
              <p>Share your profile with friends and family</p>
              
              <div className="share-link-container">
                <input 
                  type="text" 
                  value={shareLink} 
                  readOnly 
                  className="share-link-input"
                />
                <button className="copy-button" onClick={handleCopyLink}>
                  Copy
                </button>
              </div>
              
              <div className="share-buttons">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Check out my profile: ${shareLink}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-button"
                >
                  📱 Share on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

export default ProfileEdit;